import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import {
  Receipt, Calendar, Clock, DollarSign, Mail, Building,
  FileText, Download, CreditCard, AlertTriangle
} from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function MyInvoices({ customerEmail }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [payingInvoice, setPayingInvoice] = useState(null);

  useEffect(() => {
    fetchCustomerAndInvoices();
  }, [customerEmail]);

  const fetchCustomerAndInvoices = async () => {
    try {
      // First get the customer ID from email
      const customersRes = await axiosInstance.get('/customers');
      const customer = customersRes.data.find(c => c.email === customerEmail);
      
      if (customer) {
        setCustomerId(customer.id);
        const invoicesRes = await axiosInstance.get(`/customers/${customer.id}/invoices`);
        // Calculate days overdue
        const now = new Date();
        const processedInvoices = invoicesRes.data.map(inv => {
          const dueDate = new Date(inv.due_date);
          const daysOverdue = Math.floor((now - dueDate) / (1000 * 60 * 60 * 24));
          return { ...inv, days_overdue: daysOverdue };
        });
        setInvoices(processedInvoices.sort((a, b) => new Date(b.issue_date) - new Date(a.issue_date)));
      }
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status, daysOverdue) => {
    if (status === 'paid') return 'bg-green-500';
    if (status === 'cancelled') return 'bg-gray-500';
    if (status === 'draft') return 'bg-slate-400';
    if (daysOverdue > 30) return 'bg-red-600';
    if (daysOverdue > 14) return 'bg-orange-500';
    if (daysOverdue > 0) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  const getStatusLabel = (status, daysOverdue) => {
    if (status === 'paid') return 'Paid';
    if (status === 'cancelled') return 'Cancelled';
    if (status === 'partially_paid') return 'Partially Paid';
    if (status === 'draft') return 'Pending';
    if (daysOverdue > 0) return `Overdue (${daysOverdue} days)`;
    return 'Payment Due';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { timeZone: 'America/Phoenix',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePayNow = (invoice) => {
    setPayingInvoice(invoice);
    processHostedCheckout(invoice);
  };

  const processHostedCheckout = async (inv) => {
    try {
      const items = (inv.items || []).map(item => ({
        name: item.name || item.service || 'Service',
        price: Math.round(Number(item.total || item.amount || 0) * 100),
        unitQty: 1
      }));
      if (items.length === 0) {
        items.push({
          name: `Invoice ${inv.invoice_number}`,
          price: Math.round(Number(inv.balance_due || inv.total_amount || 0) * 100),
          unitQty: 1
        });
      }
      const nameParts = (inv.customer_name || '').split(' ');
      const res = await axiosInstance.post('/clover/hosted-checkout', {
        customer_email: inv.customer_email || '',
        customer_first_name: nameParts[0] || '',
        customer_last_name: nameParts.slice(1).join(' ') || '',
        items,
        invoice_id: inv.id
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start payment');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="my-invoices">
      <div className="mb-8">
        <h2 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          My Invoices
        </h2>
        <p className="text-gray-600">View and pay your invoices</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-80">Total Invoices</div>
                <div className="text-2xl font-bold">{invoices.length}</div>
              </div>
              <Receipt size={32} className="opacity-60" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-80">Paid</div>
                <div className="text-2xl font-bold">
                  {formatCurrency(invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.total_amount, 0))}
                </div>
              </div>
              <DollarSign size={32} className="opacity-60" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-80">Outstanding</div>
                <div className="text-2xl font-bold">
                  {formatCurrency(invoices.filter(i => i.status !== 'paid' && i.status !== 'cancelled').reduce((sum, i) => sum + (i.balance_due || i.total_amount), 0))}
                </div>
              </div>
              <AlertTriangle size={32} className="opacity-60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoice List */}
      <div className="space-y-4">
        {invoices.map((invoice) => (
          <Card 
            key={invoice.id} 
            className="backdrop-blur-sm bg-white/80 shadow-lg border-0 hover:shadow-xl transition-shadow cursor-pointer"
            onClick={() => {
              setSelectedInvoice(invoice);
              setShowDetailDialog(true);
            }}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-teal-100 rounded-full">
                    <Receipt className="text-teal-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{invoice.invoice_number}</h3>
                    <div className="text-sm text-gray-500 space-y-1 mt-1">
                      <p className="flex items-center gap-2">
                        <Calendar size={14} />
                        Issued: {formatDate(invoice.issue_date)}
                      </p>
                      <p className="flex items-center gap-2">
                        <Clock size={14} />
                        Due: {formatDate(invoice.due_date)}
                      </p>
                    </div>
                    {invoice.invoice_type && invoice.invoice_type !== 'full' && (
                      <Badge variant="outline" className="mt-2">
                        {invoice.invoice_type.toUpperCase()}
                        {invoice.advance_percentage && ` (${invoice.advance_percentage}%)`}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold mb-1">
                    {formatCurrency(invoice.total_amount)}
                  </div>
                  {invoice.status !== 'paid' && invoice.amount_paid > 0 && (
                    <div className="text-sm text-orange-600 mb-1">
                      Balance: {formatCurrency(invoice.balance_due)}
                    </div>
                  )}
                  <Badge className={`${getStatusColor(invoice.status, invoice.days_overdue)} text-white`}>
                    {getStatusLabel(invoice.status, invoice.days_overdue)}
                  </Badge>
                  {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                    <Button 
                      size="sm" 
                      className="mt-2 w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePayNow(invoice);
                      }}
                    >
                      <CreditCard size={14} className="mr-1" />
                      Pay Now
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {invoices.length === 0 && (
          <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Receipt size={48} className="text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No invoices yet</p>
              <p className="text-gray-400 text-sm">Your invoices will appear here</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Invoice Detail Dialog - Professional View matching Admin */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
          {selectedInvoice && (
            <div className="flex flex-col h-full max-h-[90vh]">
              {/* Header Bar */}
              <div className="flex items-center justify-between p-4 border-b bg-white sticky top-0 z-10">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <FileText className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-900">Invoice {selectedInvoice.invoice_number}</h2>
                    <p className="text-sm text-gray-500">
                      {selectedInvoice.status === 'paid' ? 'Paid' : `Balance: ${formatCurrency(selectedInvoice.balance_due || selectedInvoice.total_amount)}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${getStatusColor(selectedInvoice.status, selectedInvoice.days_overdue)} text-white`}>
                    {getStatusLabel(selectedInvoice.status, selectedInvoice.days_overdue)}
                  </Badge>
                </div>
              </div>

              {/* Invoice Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50">
                <div className="max-w-3xl mx-auto my-6">
                  {/* Invoice Paper */}
                  <div className="bg-white shadow-lg rounded-lg">
                    
                    {/* Header Section */}
                    <div className="p-8 pb-6">
                      <div className="flex justify-between items-start">
                        {/* Company Logo and Info - Left */}
                        <div>
                          <img 
                            src="https://customer-assets.emergentagent.com/job_a43a1576-2447-420f-b8ba-eeae8c53cffe/artifacts/a7oiuv0k_Black%20-%20With%20ROC%20number.png" 
                            alt="Redefyne Logo" 
                            className="h-32 w-auto mb-3"
                          />
                          <p className="text-gray-600 text-sm">2301 S Stearman Dr, Chandler, AZ 85286</p>
                          <p className="text-gray-500 text-sm">+1 602-715-1313 | support@redefyneaz.com</p>
                        </div>
                        
                        {/* Invoice Title - Right */}
                        <div className="text-right">
                          <h2 className="text-3xl font-bold text-teal-600">INVOICE</h2>
                          <p className="text-xl font-semibold text-gray-800 mt-1">#{selectedInvoice.invoice_number}</p>
                        </div>
                      </div>

                      {/* Invoice Meta */}
                      <div className="mt-6 flex justify-between">
                        <div className="flex gap-8">
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Issued</p>
                            <p className="text-sm font-medium text-gray-800">{formatDate(selectedInvoice.issue_date)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Due</p>
                            <p className="text-sm font-medium text-gray-800">{formatDate(selectedInvoice.due_date)}</p>
                          </div>
                        </div>
                        {selectedInvoice.quote_id && (
                          <div className="text-right">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Reference</p>
                            <p className="text-sm text-gray-600">Quote #{selectedInvoice.quote_id?.slice(0, 8).toUpperCase()}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="h-px bg-gray-200 mx-8" />

                    {/* Customer Section */}
                    <div className="p-8 py-6">
                      <div className="grid grid-cols-2 gap-8">
                        {/* Bill To */}
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Bill To</p>
                          <p className="font-semibold text-gray-900 text-lg">{selectedInvoice.customer_name}</p>
                          {selectedInvoice.customer_address && (
                            <p className="text-gray-600 text-sm mt-1">{selectedInvoice.customer_address}</p>
                          )}
                          {selectedInvoice.customer_phone && (
                            <p className="text-gray-600 text-sm mt-1">{selectedInvoice.customer_phone}</p>
                          )}
                          {selectedInvoice.customer_email && (
                            <p className="text-gray-600 text-sm mt-1">{selectedInvoice.customer_email}</p>
                          )}
                        </div>

                        {/* Payment Terms */}
                        <div className="text-right">
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Payment Terms</p>
                          <p className="text-lg font-medium text-gray-800">{selectedInvoice.payment_terms || 'Due in 30 days'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Line Items */}
                    <div className="px-8">
                      <table className="w-full">
                        <thead>
                          <tr className="border-y border-gray-200 bg-gray-50">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide">Item</th>
                            <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide w-20">Qty</th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide w-28">Unit Price</th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide w-28">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedInvoice.items?.length > 0 ? (
                            selectedInvoice.items.map((item, idx) => (
                              <tr key={idx} className="border-b border-gray-100">
                                <td className="py-4 px-4">
                                  <p className="font-medium text-gray-900">{item.name || item.description || item.service || 'Service'}</p>
                                  {item.description && item.name && (
                                    <p className="text-sm text-gray-500 mt-1 whitespace-pre-wrap">{item.description}</p>
                                  )}
                                </td>
                                <td className="py-4 px-4 text-center text-gray-600">{item.quantity || 1}</td>
                                <td className="py-4 px-4 text-right text-gray-600">
                                  ${Number(item.unit_price || item.rate || item.amount || 0).toFixed(2)}
                                </td>
                                <td className="py-4 px-4 text-right font-medium text-gray-900">
                                  ${Number(item.amount || item.total || (item.quantity * item.unit_price) || 0).toFixed(2)}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="py-8 text-center text-gray-500">No line items</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Totals Section */}
                    <div className="p-8 pt-4">
                      <div className="flex justify-end">
                        <div className="w-72">
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Subtotal</span>
                              <span className="font-medium">${Number(selectedInvoice.subtotal || selectedInvoice.total_amount || 0).toFixed(2)}</span>
                            </div>
                            {selectedInvoice.tax > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Tax</span>
                                <span className="font-medium">${Number(selectedInvoice.tax).toFixed(2)}</span>
                              </div>
                            )}
                            {selectedInvoice.discount > 0 && (
                              <div className="flex justify-between text-sm text-green-600">
                                <span>Discount</span>
                                <span className="font-medium">-${Number(selectedInvoice.discount).toFixed(2)}</span>
                              </div>
                            )}
                            <div className="h-px bg-gray-200 my-2" />
                            <div className="flex justify-between text-lg font-bold">
                              <span>Total</span>
                              <span>${Number(selectedInvoice.total_amount || 0).toFixed(2)}</span>
                            </div>
                            {selectedInvoice.amount_paid > 0 && (
                              <>
                                <div className="flex justify-between text-sm text-green-600">
                                  <span>Amount Paid</span>
                                  <span className="font-medium">-${Number(selectedInvoice.amount_paid).toFixed(2)}</span>
                                </div>
                                <div className="h-px bg-gray-200 my-2" />
                              </>
                            )}
                            <div className="flex justify-between text-xl font-bold text-teal-600 bg-teal-50 -mx-3 px-3 py-2 rounded">
                              <span>Balance Due</span>
                              <span>${Number(selectedInvoice.balance_due || (selectedInvoice.total_amount - (selectedInvoice.amount_paid || 0))).toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Pay Now Button - Only for unpaid invoices */}
                    {selectedInvoice.status !== 'paid' && selectedInvoice.status !== 'cancelled' && (selectedInvoice.balance_due > 0 || !selectedInvoice.amount_paid) && (
                      <div className="px-8 pb-6">
                        <div className="flex justify-center">
                          <Button 
                            size="lg" 
                            className="bg-green-600 hover:bg-green-700 text-white px-12 py-6 text-lg font-semibold rounded-lg shadow-md"
                            onClick={() => handlePayNow(selectedInvoice)}
                          >
                            <CreditCard className="w-5 h-5 mr-2" />
                            Pay ${Number(selectedInvoice.balance_due || selectedInvoice.total_amount || 0).toFixed(2)} Now
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Notes & Footer */}
                    <div className="px-8 pb-8">
                      {selectedInvoice.notes && (
                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Notes</p>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedInvoice.notes}</p>
                        </div>
                      )}
                      
                      {/* Thank You Message */}
                      <div className="text-center pt-6 border-t border-gray-100">
                        <p className="text-gray-600 text-sm">
                          Thank you for your business. Please contact us with any questions regarding this invoice.
                        </p>
                        <p className="text-gray-400 text-xs mt-2">
                          2301 S Stearman Dr, Chandler, AZ 85286 • +1 602-715-1313 • support@redefyneaz.com
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
